const chapter = {
  id: 'equilibrium',
  slug: 'equilibrium',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Equilibrium — Chemical & Ionic',
        hindi: 'साम्यावस्था — रासायनिक और आयनिक',
  },
  estReadMinutes: 80,
  difficulty: {
    english: 'Hard',
        hindi: 'कठिन',
  },
  weightage: {
    english: '3–4 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 3-4 प्रश्न',
  },
  syllabusTags: ['Physical Chemistry', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'chemical-eq',
      title: {
        english: 'Chemical Equilibrium — $K_c$ and $K_p$',
        hindi: 'रासायनिक संतुलन - $K_c$ और $K_p$',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'A reversible reaction reaches **dynamic equilibrium** when forward and backward rates become equal. Concentrations of all species remain constant (but reactions continue both ways).',
        hindi: 'जब आगे और पीछे की दरें समान हो जाती हैं तो एक प्रतिवर्ती प्रतिक्रिया **गतिशील संतुलन** पर पहुंच जाती है। सभी प्रजातियों की सांद्रता स्थिर रहती है (लेकिन प्रतिक्रियाएँ दोनों तरह से जारी रहती हैं)।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Law of mass action',
        hindi: 'सामूहिक कार्रवाई का कानून',
          },
          statement: {
            english: `For $aA + bB \\rightleftharpoons cC + dD$:
$$K_c = \\dfrac{[C]^c[D]^d}{[A]^a[B]^b}.$$
Gases: $K_p = \\dfrac{P_C^c P_D^d}{P_A^a P_B^b}$. Relation: $K_p = K_c (RT)^{\\Delta n_g}$.`,
            hindi: '$aA + bB \\rightleftharpoons cC + dD$ के लिए: $$K_c = \\dfrac{[C]^c[D]^d}{[A]^a[B]^b}.$$ गैसें: $K_p = \\dfrac{P_C^c P_D^d}{P_A^a P_B^b}$. संबंध: $K_p = K_c (RT)^{\\Delta n_g}$.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Pure solids and pure liquids are **not** included in $K$ (their activity = 1).',
        hindi: 'शुद्ध ठोस और शुद्ध तरल पदार्थ $K$ (उनके activity = 1)) में **नहीं** शामिल हैं।',
            },
            {
              english: 'Reverse reaction: $K\' = 1/K$. Reaction multiplied by $n$: $K\' = K^n$. Add reactions: multiply $K$\'s.',
        hindi: 'विपरीत प्रतिक्रिया: $K\\\' = 1/K$। प्रतिक्रिया को $n$ से गुणा किया गया: $K\\\' = K^n$। प्रतिक्रियाएँ जोड़ें: $K$ को गुणा करें। **प्रतिक्रिया भागफल $K\\\' = 1/K$**: $n$ के समान अभिव्यक्ति लेकिन वर्तमान सांद्रता के साथ। तुलना करें:',
            },
            {
              english: '**Reaction quotient $Q$**: same expression as $K$ but with current concentrations. Compare:',
        hindi: '**प्रतिक्रिया भागफल $Q$**: $K$ के समान अभिव्यक्ति लेकिन वर्तमान सांद्रता के साथ। तुलना करना:',
            },
            {
              english: '$Q < K$ → forward; $Q > K$ → backward; $Q = K$ → equilibrium.',
        hindi: '$Q < K$ → आगे; $Q > K$ → पिछड़ा; $Q = K$ → संतुलन।',
            },
            {
              english: 'Magnitude: large $K$ ⇒ products favoured; small $K$ ⇒ reactants favoured.',
        hindi: 'परिमाण: बड़े $K$ ⇒ पसंदीदा उत्पाद; छोटे $K$ ⇒ पसंदीदा अभिकारक।',
            },
            {
              english: 'Linked to thermodynamics: $\\Delta G^\\circ = -RT\\ln K$.',
        hindi: 'थर्मोडायनामिक्स से जुड़ा: $\\Delta G^\\circ = -RT\\ln K$।',
            },
          ],
        },
      ],
    },
    {
      id: 'le-chatelier',
      title: {
        english: 'Le Chatelier\'s Principle',
        hindi: 'ले शाटेलिए का सिद्धान्त',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'When a system at equilibrium is disturbed by change in concentration, pressure, or temperature, it shifts in the direction that partly **opposes** the disturbance.',
        hindi: 'जब संतुलन पर एक प्रणाली एकाग्रता, दबाव या तापमान में परिवर्तन से परेशान होती है, तो यह उस दिशा में स्थानांतरित हो जाती है जो आंशिक रूप से गड़बड़ी का **विरोध** करती है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Concentration**: increasing reactant ⇒ shift forward. Increasing product ⇒ shift backward.',
        hindi: '**एकाग्रता**: अभिकारक बढ़ाना ⇒ आगे बढ़ना। उत्पाद बढ़ाना ⇒ पीछे की ओर शिफ्ट होना।',
            },
            {
              english: '**Pressure** (gases): increase $P$ ⇒ shifts to side with **fewer moles of gas**. No effect if $\\Delta n_g = 0$.',
        hindi: '**दबाव** (गैसें): वृद्धि $P$ ⇒ **गैस के कम मोल** के साथ पक्ष में बदलाव। यदि $\\Delta n_g = 0$ हो तो कोई प्रभाव नहीं।',
            },
            {
              english: '**Temperature**: For exothermic ($\\Delta H < 0$), increasing $T$ shifts backward, $K$ decreases. For endothermic, opposite.',
        hindi: '**तापमान**: ऊष्माक्षेपी ($\\Delta H < 0$) के लिए, $T$ बढ़ने से पीछे की ओर खिसक जाता है, $K$ घट जाता है। एंडोथर्मिक के लिए, विपरीत।',
            },
            {
              english: '**Catalyst**: speeds both forward and reverse equally — does **not** shift equilibrium, only reaches it faster.',
        hindi: '**उत्प्रेरक**: आगे और पीछे दोनों की गति समान होती है - संतुलन **नहीं** बदलता है, केवल तेजी से पहुंचता है।',
            },
            {
              english: '**Inert gas at constant V**: no change. **Inert gas at constant P**: equivalent to dilution → shifts to side with more moles of gas.',
        hindi: '**निरंतर V पर अक्रिय गैस**: कोई परिवर्तन नहीं। **निरंतर P पर अक्रिय गैस**: तनुकरण के बराबर → गैस के अधिक मोल के साथ पक्ष में स्थानांतरित हो जाती है।',
            },
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'Industrial: Haber process (N₂ + 3H₂ ⇌ 2NH₃, exo): high P, moderate T (~700 K), Fe catalyst. Contact: SO₂ + 1/2 O₂ ⇌ SO₃, V₂O₅, ~720 K.',
        hindi: 'औद्योगिक: हैबर प्रक्रिया (N₂ + 3H₂ ⇌ 2NH₃, exo): उच्च P, मध्यम T (~700 K), Fe उत्प्रेरक। संपर्क: SO₂ + 1/2 O₂ ⇌ SO₃, V₂O₅, ~720 K.',
          },
        },
      ],
    },
    {
      id: 'ionic-eq',
      title: {
        english: 'Ionic Equilibrium — Acids, Bases, pH',
        hindi: 'आयनिक साम्य — अम्ल, क्षार, pH',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Arrhenius**: acid releases H⁺ in water; base releases OH⁻.',
        hindi: '**अरहेनियस**: एसिड पानी में H⁺ छोड़ता है; आधार OH⁻ जारी करता है।',
            },
            {
              english: '**Brønsted–Lowry**: acid = proton donor; base = proton acceptor. Conjugate pairs differ by H⁺.',
        hindi: '**ब्रोंस्टेड-लोरी**: एसिड = प्रोटॉन दाता; आधार = प्रोटॉन स्वीकर्ता। संयुग्मी जोड़े H⁺ से भिन्न होते हैं।',
            },
            {
              english: '**Lewis**: acid = electron-pair acceptor; base = electron-pair donor.',
        hindi: '**लुईस**: अम्ल = इलेक्ट्रॉन-युग्म स्वीकर्ता; आधार = इलेक्ट्रॉन-युग्म दाता।',
            },
            {
              english: '**Ionisation constant**: $K_a$ for acid ($HA \\rightleftharpoons H^+ + A^-$); $K_b$ for base ($B + H_2O \\rightleftharpoons BH^+ + OH^-$).',
        hindi: '**आयनिकरण स्थिरांक**: अम्ल के लिए $K_a$ ($HA \\rightleftharpoons H^+ + A^-$); आधार के लिए $K_b$ ($B + H_2O \\rightleftharpoons BH^+ + OH^-$)।',
            },
            {
              english: 'For a conjugate acid-base pair: $K_a \\cdot K_b = K_w$.',
        hindi: 'संयुग्मी अम्ल-क्षार युग्म के लिए: $K_a \\cdot K_b = K_w$।',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Self-ionisation of water & pH',
        hindi: 'पानी और पीएच का स्व-आयनीकरण',
          },
          statement: {
            english: `$K_w = [H^+][OH^-] = 1.0\\times 10^{-14}$ at 25 °C.
$\\text{pH} = -\\log[H^+]$, $\\text{pOH} = -\\log[OH^-]$, **$\\text{pH} + \\text{pOH} = 14$** at 25 °C.`,
            hindi: '25 डिग्री सेल्सियस पर $K_w = [H^+][OH^-] = 1.0\\times 10^{-14}$। $\\text{pH} = -\\log[H^+]$, $\\text{pOH} = -\\log[OH^-]$, **$\\text{pH} + \\text{pOH} = 14$** 25 डिग्री सेल्सियस पर।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Strong acid/base: complete ionisation. $[H^+] = c$; pH from $c$ directly.',
        hindi: 'मजबूत acid/base: पूर्ण आयनीकरण। $[H^+] = c$; पीएच सीधे $c$ से।',
            },
            {
              english: 'Weak acid: $[H^+] \\approx \\sqrt{K_a c}$. Degree of dissociation $\\alpha = \\sqrt{K_a/c}$ (Ostwald dilution law).',
        hindi: 'कमजोर अम्ल: $[H^+] \\approx \\sqrt{K_a c}$। पृथक्करण की डिग्री $\\alpha = \\sqrt{K_a/c}$ (ओस्टवाल्ड कमजोर पड़ने का कानून)।',
            },
            {
              english: 'Weak base: $[OH^-] \\approx \\sqrt{K_b c}$.',
        hindi: 'कमज़ोर आधार: $[OH^-] \\approx \\sqrt{K_b c}$.',
            },
            {
              english: 'Polybasic acid (H₂CO₃, H₃PO₄): successive $K_{a1} > K_{a2} > K_{a3}$, generally by factor of $10^{4-5}$.',
        hindi: 'पॉलीबेसिक एसिड (H₂CO₃, H₃PO₄): क्रमिक $K_{a1} > K_{a2} > K_{a3}$, आम तौर पर $10^{4-5}$ के कारक से।',
            },
          ],
        },
      ],
    },
    {
      id: 'buffers-hydrolysis',
      title: {
        english: 'Buffers & Salt Hydrolysis',
        hindi: 'बफर और लवण जल-अपघटन',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Buffer solution',
        hindi: 'बफर द्रावण',
          },
          body: {
            english: `A solution that resists change in pH on small addition of acid or base. Two types:
— **Acidic buffer**: weak acid + its conjugate salt (CH₃COOH + CH₃COONa).
— **Basic buffer**: weak base + its conjugate salt (NH₄OH + NH₄Cl).`,
            hindi: 'एक समाधान जो अम्ल या क्षार की थोड़ी मात्रा मिलाने पर pH में परिवर्तन का प्रतिरोध करता है। दो प्रकार: - **अम्लीय बफर**: कमजोर एसिड + इसका संयुग्मित नमक (CH₃COOH + CH₃COONa)। - **बेसिक बफर**: कमजोर आधार + इसका संयुग्मित नमक (NH₄OH + NH₄Cl)।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Henderson–Hasselbalch equation',
        hindi: 'हेंडरसन-हसलबल्च समीकरण',
          },
          statement: {
            english: `Acid buffer: $\\text{pH} = pK_a + \\log\\dfrac{[\\text{salt}]}{[\\text{acid}]}$.
Base buffer: $\\text{pOH} = pK_b + \\log\\dfrac{[\\text{salt}]}{[\\text{base}]}$.
Maximum buffer capacity at $\\text{pH} = pK_a$ (i.e. when [salt] = [acid]).`,
            hindi: 'एसिड बफर: $\\text{pH} = pK_a + \\log\\dfrac{[\\text{salt}]}{[\\text{acid}]}$। बेस बफ़र: $\\text{pOH} = pK_b + \\log\\dfrac{[\\text{salt}]}{[\\text{base}]}$. अधिकतम बफर क्षमता $\\text{pH} = pK_a$ पर (अर्थात् जब [नमक] = [अम्ल])।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Salt of strong acid + strong base** (NaCl): no hydrolysis, pH = 7.',
        hindi: '**मजबूत एसिड का नमक + मजबूत क्षार** (NaCl): कोई हाइड्रोलिसिस नहीं, pH = 7।',
            },
            {
              english: '**Strong acid + weak base** (NH₄Cl): cation hydrolyses, **acidic** solution. $\\text{pH} = 7 - \\tfrac12 (pK_b + \\log c)$.',
        hindi: '**मजबूत अम्ल + कमजोर आधार** (NH₄Cl): धनायन हाइड्रोलाइज, **अम्लीय** घोल। $\\text{pH} = 7 - \\tfrac12 (pK_b + \\log c)$.',
            },
            {
              english: '**Weak acid + strong base** (CH₃COONa): anion hydrolyses, **basic**. $\\text{pH} = 7 + \\tfrac12 (pK_a + \\log c)$.',
        hindi: '**कमजोर एसिड + मजबूत आधार** (CH₃COONa): आयन हाइड्रोलाइज, **बेसिक**। $\\text{pH} = 7 + \\tfrac12 (pK_a + \\log c)$.',
            },
            {
              english: '**Weak acid + weak base** (CH₃COONH₄): $\\text{pH} = 7 + \\tfrac12 (pK_a - pK_b)$. Independent of concentration.',
        hindi: '**कमजोर एसिड + कमजोर आधार** (CH₃COONH₄): $\\text{pH} = 7 + \\tfrac12 (pK_a - pK_b)$। एकाग्रता से स्वतंत्र.',
            },
          ],
        },
      ],
    },
    {
      id: 'ksp',
      title: {
        english: 'Solubility Product & Common-Ion Effect',
        hindi: 'विलेयता गुणनफल और समआयन प्रभाव',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Solubility product',
        hindi: 'घुलनशीलता उत्पाद',
          },
          statement: {
            english: `For sparingly soluble salt $A_x B_y \\rightleftharpoons xA^{y+} + yB^{x-}$ with molar solubility $s$:
$K_{sp} = (xs)^x (ys)^y = x^x y^y \\cdot s^{(x+y)}$.`,
            hindi: 'दाढ़ घुलनशीलता के साथ अल्प घुलनशील नमक $A_x B_y \\rightleftharpoons xA^{y+} + yB^{x-}$ के लिए $s$: $K_{sp} = (xs)^x (ys)^y = x^x y^y \\cdot s^{(x+y)}$.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'AB type (AgCl): $K_{sp} = s^2$. AB₂ (CaF₂): $K_{sp} = 4s^3$. A₂B (Ag₂CrO₄): $4s^3$. A₂B₃ (Ca₃(PO₄)₂ analogue, A₂B₃): $108\\,s^5$.',
        hindi: 'AB प्रकार (AgCl): $K_{sp} = s^2$। AB₂ (CaF₂): $K_{sp} = 4s^3$. A₂B (Ag₂CrO₄): $4s^3$। A₂B₃ (Ca₃(PO₄)₂ एनालॉग, A₂B₃): $108\\,s^5$। **सामान्य-आयन प्रभाव**: एक सामान्य आयन (ले चेटेलियर) साझा करने वाले घुलनशील यौगिक को जोड़ने से अल्प घुलनशील नमक की घुलनशीलता **कम** हो जाती है। **आयनिक उत्पाद $K_{sp} = s^2$ बनाम $K_{sp} = 4s^3$**: $4s^3$ असंतृप्त; $108\\,s^5$ संतृप्त; अवक्षेप रूप।',
            },
            {
              english: '**Common-ion effect**: solubility of a sparingly soluble salt is **decreased** by adding a soluble compound that shares a common ion (Le Chatelier).',
        hindi: '**सामान्य-आयन प्रभाव**: एक सामान्य आयन (ले चेटेलियर) साझा करने वाले घुलनशील यौगिक को जोड़ने से अल्प घुलनशील नमक की घुलनशीलता **कम** हो जाती है।',
            },
            {
              english: '**Ionic product $Q$ vs $K_{sp}$**: $Q < K_{sp}$ unsaturated; $Q = K_{sp}$ saturated; $Q > K_{sp}$ precipitate forms.',
        hindi: '**आयनिक उत्पाद $Q$ बनाम $K_{sp}$**: $Q < K_{sp}$ असंतृप्त; $Q = K_{sp}$ संतृप्त; $Q > K_{sp}$ अवक्षेप रूप।',
            },
            {
              english: 'Used for selective precipitation, qualitative analysis groups.',
        hindi: 'चयनात्मक वर्षा, गुणात्मक विश्लेषण समूहों के लिए उपयोग किया जाता है।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: '$K_{sp}$ of AgCl = $1.6\\times 10^{-10}$. Solubility (mol/L)?',
        hindi: 'AgCl का $K_{sp}$ = $1.6\\times 10^{-10}$. घुलनशीलता (मोल/एल)?',
          },
          solution: {
            english: '$s = \\sqrt{K_{sp}} = 1.26\\times 10^{-5}$ mol/L.',
        hindi: '$s = \\sqrt{K_{sp}} = 1.26\\times 10^{-5}$ mol/L.',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'A buffer is made from 0.2 M CH₃COOH and 0.1 M CH₃COONa. ($pK_a = 4.74$). pH?',
        hindi: 'एक बफर 0.2 M CH₃COOH और 0.1 M CH₃COONa से बनाया गया है। ($pK_a = 4.74$). पीएच?',
          },
          solution: {
            english: '$\\text{pH} = 4.74 + \\log(0.1/0.2) = 4.74 - 0.30 = 4.44$.',
        hindi: '$\\text{pH} = 4.74 + \\log(0.1/0.2) = 4.74 - 0.30 = 4.44$।',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'K_p = K_c (RT)^{\\Delta n_g}',
      desc: {
        english: '$K_p$–$K_c$ relation',
        hindi: '$K_p$–$K_c$ संबंध',
      },
    },
    {
      latex: '\\Delta G^\\circ = -RT \\ln K',
      desc: {
        english: 'Gibbs–K',
        hindi: 'गिब्स-के',
      },
    },
    {
      latex: 'K_w = [H^+][OH^-] = 10^{-14}',
      desc: {
        english: 'Ionic product of water',
        hindi: 'पानी का आयनिक उत्पाद',
      },
    },
    {
      latex: '\\alpha = \\sqrt{K_a/c}',
      desc: {
        english: 'Ostwald dilution law',
        hindi: 'ओस्टवाल्ड तनुकरण नियम',
      },
    },
    {
      latex: '\\text{pH} = pK_a + \\log([S]/[A])',
      desc: {
        english: 'Henderson',
        hindi: 'हेंडरसन',
      },
    },
    {
      latex: 'K_a \\cdot K_b = K_w',
      desc: {
        english: 'Conjugate pair',
        hindi: 'संयुग्मी युग्म',
      },
    },
    {
      latex: 'K_{sp} = x^x y^y s^{x+y}',
      desc: {
        english: 'Solubility product',
        hindi: 'घुलनशीलता उत्पाद',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: '$K_p, K_c$',
      problem: {
        english: 'For $N_2 + 3H_2 \\rightleftharpoons 2NH_3$, relation between $K_p$ and $K_c$?',
        hindi: '$N_2 + 3H_2 \\rightleftharpoons 2NH_3$ के लिए, $K_p$ और $K_c$ के बीच संबंध?',
      },
      answer: {
        english: '$K_p = K_c (RT)^{-2}$',
        hindi: '$K_p = K_c (RT)^{-2}$',
      },
      solution: {
        english: '$\\Delta n_g = 2 - 4 = -2$.',
        hindi: '$\\Delta n_g = 2 - 4 = -2$.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'pH',
      problem: {
        english: 'pH of $10^{-3}$ M HCl?',
        hindi: '$10^{-3}$ M HCl का pH?',
      },
      answer: {
        english: '$3$',
        hindi: '$3$',
      },
      solution: {
        english: '$[H^+] = 10^{-3}$.',
        hindi: '$[H^+] = 10^{-3}$।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Buffer',
      problem: {
        english: 'pH of buffer with [acid] = [salt] of $pK_a = 5$?',
        hindi: '$pK_a = 5$ के [एसिड] = [नमक] के साथ बफर का pH?',
      },
      answer: {
        english: '$5$',
        hindi: '$5$',
      },
      solution: {
        english: 'Henderson: $\\log 1 = 0$.',
        hindi: 'हेंडरसन: $\\log 1 = 0$।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Ostwald',
      problem: {
        english: 'A weak acid $K_a = 1.8\\times 10^{-5}$ is 0.01 M. Degree of dissociation?',
        hindi: 'एक कमजोर एसिड $K_a = 1.8\\times 10^{-5}$ 0.01 एम है। पृथक्करण की डिग्री?',
      },
      answer: {
        english: '$\\approx 4.2\\%$',
        hindi: '$\\approx 4.2\\\\%$',
      },
      solution: {
        english: '$\\alpha = \\sqrt{1.8\\times 10^{-5}/10^{-2}} = \\sqrt{1.8\\times 10^{-3}} \\approx 0.042$.',
        hindi: '$\\alpha = \\sqrt{1.8\\times 10^{-5}/10^{-2}} = \\sqrt{1.8\\times 10^{-3}} \\approx 0.042$.',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Ksp',
      problem: {
        english: '$K_{sp}$ of CaF₂ is $4\\times 10^{-11}$. Molar solubility?',
        hindi: 'CaF₂ का $K_{sp}$ $4\\times 10^{-11}$ है। मोलर घुलनशीलता?',
      },
      answer: {
        english: '$\\approx 2.15\\times 10^{-4}$ mol/L',
        hindi: '$\\approx 2.15\\times 10^{-4}$ mol/L',
      },
      solution: {
        english: '$K_{sp} = 4s^3 \\Rightarrow s = (10^{-11})^{1/3} = 2.15\\times 10^{-4}$.',
        hindi: '$K_{sp} = 4s^3 \\Rightarrow s = (10^{-11})^{1/3} = 2.15\\times 10^{-4}$.',
      },
    },
  ],
  next: {
    slug: 'periodic-classification',
    title: 'Classification of Elements & Periodicity',
  },
};

export default chapter;
